import { useState, Dispatch, SetStateAction } from "react";
import styled from "styled-components/macro";
import {
  Box,
  BoxTitle,
  BoxHeader,
  BoxNavLeft,
  BoxNavRight,
  BoxNavButton,
  BoxContent,
  BoxFooter,
  BoxNav,
} from "./Box";
import { ChevronLeft, ChevronRight } from "./Icons";
import { THEME_COLOR } from "../vars";
import classNames from "classnames";
import Input from "./Input";
import Button from "./Button";
import Overlay, { OverlayTrigger } from "./Overlay";
import moment from "moment";
import useMoment from "../hooks/use-moment";

function momentSetMeridiem(this: moment.Moment, newMeridiem: "AM" | "PM") {
  if (newMeridiem.toUpperCase() === "AM" && this.hours() >= 12) {
    this.hours(this.hours() - 12);
  } else if (newMeridiem.toUpperCase() === "PM" && this.hours() < 12) {
    this.hours(this.hours() + 12);
  }

  return this;
}

type DatePickerProps = {
  value: Date | null | undefined;
  onChange: Dispatch<SetStateAction<Date | null | undefined>>;
  maxDate?: number;
  minDate?: number;
  mode?: "datetime" | "time" | "date";
  clearable?: boolean;
  hide?: () => void;
};

function DatePicker({
  value,
  onChange,
  maxDate = Infinity,
  minDate = 0,
  mode = "datetime", //datetime, time, date
  clearable = false,
  hide,
}: DatePickerProps) {
  const isSet = Boolean(value);
  const moment = useMoment();
  if (!value) value = new Date(Date.now());
  if (!(value instanceof Date)) value = new Date(value);

  const [focusedDate, setFocusedDate] = useState(value);
  const [monthOffset, setMonthOffset] = useState(0);
  const nowMoment = moment(focusedDate).add(monthOffset, "months").toDate();

  const now = new Date(nowMoment);

  const calendarDays = [];
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 0; i < firstOfMonth.getDay(); i++) calendarDays.push(null);

  const numDays = new Date(
    now.getFullYear(),
    now.getMonth() + 1, // next month
    0 // last day of previous month
  ).getDate();

  for (let i = 1; i <= numDays; i++) calendarDays.push(i);

  const monthName = moment.months()[now.getMonth()];

  const momentValue = moment(value);
  const valueDateString = momentValue.format("MM-DD-YYYY");

  function clampDate(date: Date) {
    const timestamp = Number(date);
    if (timestamp < minDate) return new Date(minDate);
    if (timestamp > maxDate) return new Date(maxDate);
    else return date;
  }

  type Day = {
    date: number;
    dateString: string;
    isSelected: boolean;
    onClick: () => void;
    disabled: boolean;
  };

  const weeks = calendarDays.reduce(
    (weeks, day) => {
      const week = weeks[weeks.length - 1];
      if (day === null) {
        const payload = null;
        if (week.length < 7) week.push(payload);
        else weeks.push([payload]);
        return weeks;
      }

      const momentInstance = moment([
        now.getFullYear(),
        now.getMonth(),
        day,
        0,
        0,
        0,
        0,
      ]);

      const date = momentInstance.toDate();
      const dateString = momentInstance.format("MM-DD-YYYY");

      const onClick = () => {
        onChange((value) => {
          const date = clampDate(
            moment(value || new Date())
              .clone()
              .dayOfYear(momentInstance.dayOfYear())
              .year(momentInstance.year())
              .toDate()
          );

          setFocusedDate(date);
          return date;
        });
        setMonthOffset(0);
      };

      const payload: Day = {
        date: day,
        dateString,
        isSelected: dateString === valueDateString,
        onClick,
        disabled: !(date.getTime() >= minDate && maxDate >= date.getTime()),
      };

      if (week.length < 7) week.push(payload);
      else weeks.push([payload]);
      return weeks;
    },
    [[]] as (Day | null)[][]
  );

  function setHours(hoursString: string) {
    let hours = Number(hoursString);
    onChange((value) => {
      const momentValue = moment(value || new Date());

      if (momentValue.hours() >= 12) hours = hours + 12;
      if (hours === 24) hours = 0;

      const date = clampDate(momentValue.hours(hours).toDate());

      setFocusedDate(date);

      return date;
    });
    setMonthOffset(0);
  }

  function setMinutes(minutesString: string) {
    let minutes = Number(minutesString);
    onChange((value) => {
      const date = clampDate(
        moment(value || new Date())
          .minutes(minutes)
          .toDate()
      );

      setFocusedDate(date);

      return date;
    });
    setMonthOffset(0);
  }

  function setMeridiem(meridiem: string) {
    onChange((value) => {
      const date = clampDate(
        momentSetMeridiem
          .call(moment(value || new Date()), meridiem as "AM" | "PM")
          .toDate()
      );

      setFocusedDate(date);
      return date;
    });
    setMonthOffset(0);
  }

  return (
    <Container>
      {Boolean(mode === "date" || mode === "datetime") && (
        <BoxHeader>
          <BoxNavLeft>
            <BoxNavButton
              onClick={() => setMonthOffset((offset) => offset - 1)}
            >
              <ChevronLeft />
            </BoxNavButton>
          </BoxNavLeft>
          <BoxTitle>
            {monthName} {now.getFullYear()}
          </BoxTitle>
          <BoxNavRight>
            <BoxNavButton
              onClick={() => setMonthOffset((offset) => offset + 1)}
            >
              <ChevronRight />
            </BoxNavButton>
          </BoxNavRight>
        </BoxHeader>
      )}
      <BoxContent>
        <PickerContainer>
          {Boolean(mode === "date" || mode === "datetime") && (
            <Table>
              <thead>
                <tr>
                  <th>S</th>
                  <th>M</th>
                  <th>T</th>
                  <th>W</th>
                  <th>TH</th>
                  <th>F</th>
                  <th>S</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((days, weekIndex) => (
                  <tr key={weekIndex}>
                    {days.map((day, dayIndex) =>
                      day === null ? (
                        <td key={dayIndex} />
                      ) : (
                        <td key={dayIndex}>
                          <a
                            href="#date"
                            onClick={(e) => {
                              e.preventDefault();
                              if (day.disabled) return;
                              day.onClick();
                              if (mode !== "datetime" && hide) hide();
                            }}
                            className={classNames({
                              selected: day.isSelected,
                              disabled: day.disabled,
                            })}
                          >
                            <span
                              style={{ zIndex: 1000, position: "relative" }}
                            >
                              {day.date}
                            </span>
                          </a>
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {Boolean(mode === "time" || mode === "datetime") && (
            <TimePicker>
              <Input
                type="select"
                label="Hours"
                value={String(momentValue.hours() % 12)}
                onChange={setHours}
              >
                {Array.from({ length: 12 }).map((_, index) => (
                  <option value={(index + 1) % 12} key={index}>
                    {String(index + 1).padStart(2, "0")}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                label="Minutes"
                value={String(momentValue.minutes())}
                onChange={setMinutes}
              >
                {Array.from({ length: 60 }).map((_, index) => (
                  <option value={index} key={index}>
                    {String(index).padStart(2, "0")}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                label={<>&nbsp;</>}
                value={momentValue.hours() >= 12 ? "PM" : "AM"}
                onChange={setMeridiem}
                data-testid="meridiem"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </Input>
              <Button
                variant="link"
                onClick={() => {
                  if (hide) hide();
                }}
                style={{ transform: "translateY(10px)" }}
              >
                Done
              </Button>
            </TimePicker>
          )}
        </PickerContainer>
      </BoxContent>
      {Boolean(clearable && isSet) && (
        <BoxFooter style={{ padding: 0 }} white stacked>
          <BoxNav>
            <Button
              variant="link"
              onClick={() => {
                onChange(null);
                if (hide) hide();
              }}
            >
              Clear
            </Button>
          </BoxNav>
        </BoxFooter>
      )}
    </Container>
  );
}

export function DatePickerOverlay({
  trigger,
  right = false,
  bottom = true,
  ...props
}: DatePickerProps & {
  trigger: OverlayTrigger;
  right?: boolean;
  bottom?: boolean;
}) {
  return (
    <Overlay
      border
      position={{
        ...(bottom
          ? { bottom: ({ height }) => height + 4 }
          : { top: ({ height }) => height + 4 }),
        ...(right ? { right: () => 0 } : { left: () => 0 }),
      }}
      trigger={trigger}
      content={({ hide }) => <DatePicker {...props} hide={hide} />}
    />
  );
}

const Container = styled(Box)`
  display: inline-flex;
  width: auto;
  min-width: 300px;
  border: 0;
  ${BoxContent} {
    padding: 4px;
  }
  ${BoxTitle} {
    text-align: center;
    padding: 0;
  }
`;

const PickerContainer = styled.div``;

const TimePicker = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  & > * {
    flex-grow: 1;
    flex-basis: 0;
    margin-bottom: 0 !important;
    padding: 8px;
  }
`;

const Table = styled.table`
  table-layout: fixed;
  color: #555;
  width: 300px;
  padding: 0px 0px;
  border-collapse: separate;
  border-spacing: 0;
  thead {
    th {
      border-bottom: 1px solid #eee;
      font-weight: normal;
      font-size: 10px;
      color: #aaa;
      padding-top: 5px;
      padding-bottom: 5px;
      text-align: center;
    }
  }
  a {
    cursor: pointer;
    display: inline-block;
    width: 100%;
    height: 100%;
    padding: 5px 0;
    line-height: 24px;
    border-radius: 2px;
    &:not(.disabled) {
      &:hover:not(.selected) {
        background: rgba(0, 0, 0, 0.05);
      }
      &:active {
        background: rgba(0, 0, 0, 0.1);
      }
      &.selected {
        box-shadow: 0px 0px 0px 2px ${THEME_COLOR};
      }
    }
    &.disabled {
      opacity: 0.5;
    }
  }
  td {
    text-align: center;
    position: relative;
    z-index: 1;
    transition-duration: 0.2s;
    border-radius: 2px;
    vertical-align: middle;
    a:link {
      color: inherit;
      text-decoration: none;
    }
    a:visited {
      color: inherit;
    }
  }
`;

export default DatePicker;
